/* eslint-disable react-hooks/set-state-in-effect */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { ForbiddenError, ForbiddenUserInfo } from '@bmwe/data';
import { useMeQuery } from '@bmwe/data/shared';
import { toActiveUser } from '../mappers';
import { ActiveUser } from '../types';

interface UserContextState<T extends ActiveUser> {
  user: T | null;
  isLoading: boolean;
  isForbidden: boolean;
  forbiddenUserInfo?: ForbiddenUserInfo;
  setCurrentUser: (user: T) => void;
  clearUser: () => void;
}

const initialState: UserContextState<ActiveUser> = {
  user: null,
  isLoading: false,
  isForbidden: false,
  setCurrentUser: () => {
    // UserContext not initialized
  },
  clearUser: () => {
    // UserContext not initialized
  },
};

const UserContext = createContext<UserContextState<ActiveUser>>(initialState);

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const [user, setUser] = useState<ActiveUser | null>(null);
  const { data, isLoading, error } = useMeQuery();
  const isForbidden = error instanceof ForbiddenError;
  const forbiddenUserInfo = isForbidden ? error.userInfo : undefined;

  useEffect(() => {
    if (data?.me) {
      setUser(toActiveUser(data.me));
    }
  }, [data]);

  const setCurrentUser = useCallback((user: ActiveUser) => {
    setUser(user);
  }, []);

  const clearUser = useCallback(() => {
    setUser(null);
  }, []);

  const contextValue = useMemo(
    () => ({
      user,
      isLoading,
      isForbidden,
      forbiddenUserInfo,
      setCurrentUser,
      clearUser,
    }),
    [
      user,
      isLoading,
      isForbidden,
      forbiddenUserInfo,
      setCurrentUser,
      clearUser,
    ],
  );

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);

  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }

  return context;
}
